/**
 * The run diary: trace events of the run (and of its bags) in time order, with repeated bag steps
 * folded into one line ("Bag scanned in × 12").
 */
import type { RunEvent } from '../../api/process'

export const BIZ_STEP_LABEL: Record<string, string> = {
  RUN_OPENED: 'Run opened',
  RUN_CANCELLED: 'Run cancelled',
  INPUT_ISSUED: 'Bag issued to the run',
  INPUT_CONSUMED: 'Bag scanned in',
  CLEANOUT_CONFIRMED: 'Cleanout confirmed',
  OUTPUT_TAGGED: 'Output sack recorded',
  OUTPUT_VOIDED: 'Output sack voided',
  MIXING_REPORTED: 'Mixing reported',
  RUN_CLOSED: 'Run closed',
  BATCH_CREATED: 'Batches made',
  BALANCE_CHECKED: 'Balance checked',
  CORRECTION: 'Correction',
  QUARANTINE_CLEARED: 'Hold lifted',
  QUARANTINED: 'Put on hold',
  SAMPLE_SEALED: 'QC sample sealed',
  STREAM_DOWNGRADED: 'Moved to ordinary',
}

/** Steps that happen once per bag or sack and are folded together. */
const FOLDED = new Set(['INPUT_ISSUED', 'INPUT_CONSUMED', 'OUTPUT_TAGGED', 'QUARANTINE_CLEARED', 'QUARANTINED'])

export interface DiaryLine {
  id: string
  bizStep: string
  at: string | null
  until: string | null
  count: number
  actorId: string | null
  events: RunEvent[]
}

export function diaryLines(events: RunEvent[]): DiaryLine[] {
  const sorted = [...events].sort((a, b) => (a.occurredAt ?? '').localeCompare(b.occurredAt ?? ''))
  const out: DiaryLine[] = []
  for (const e of sorted) {
    const last = out[out.length - 1]
    if (last && FOLDED.has(e.bizStep) && last.bizStep === e.bizStep && last.actorId === e.actorId) {
      last.count += 1
      last.until = e.occurredAt
      last.events.push(e)
      continue
    }
    out.push({ id: e.id, bizStep: e.bizStep, at: e.occurredAt, until: e.occurredAt, count: 1, actorId: e.actorId, events: [e] })
  }
  return out
}

const humanize = (code: string) => {
  const s = code.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function diaryTitle(l: DiaryLine): string {
  const base = BIZ_STEP_LABEL[l.bizStep] ?? humanize(l.bizStep)
  if (l.bizStep === 'CORRECTION') {
    const kind = l.events[0]?.payload.kind
    const KIND: Record<string, string> = {
      REWEIGH_OUTPUT: 'output sack weighed again',
      ADD_OUTPUT: 'sack added',
      ADD_INPUT: 'bag added',
      VOID_OUTPUT: 'output sack voided',
      MOVE_TO_CONV: 'outputs moved to ordinary',
    }
    return typeof kind === 'string' && KIND[kind] ? `${base} · ${KIND[kind]}` : base
  }
  if (l.bizStep === 'BALANCE_CHECKED') {
    const r = l.events[0]?.payload.result
    return r === 'BALANCED' ? `${base} · balanced` : r === 'EXCEPTION' ? `${base} · out of range` : base
  }
  return l.count > 1 ? `${base} × ${l.count}` : base
}

export function diaryTone(l: DiaryLine): 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent' {
  const p = l.events[0]?.payload ?? {}
  if (l.bizStep === 'BALANCE_CHECKED') return p.result === 'BALANCED' ? 'ok' : 'warn'
  if (l.bizStep === 'CORRECTION') return 'accent'
  if (l.bizStep === 'MIXING_REPORTED' || l.bizStep === 'RUN_CANCELLED') return 'danger'
  if (l.bizStep === 'RUN_OPENED' || l.bizStep === 'RUN_CLOSED') return 'info'
  if (l.bizStep === 'QUARANTINE_CLEARED') return 'ok'
  return 'neutral'
}
